/**
 * Atlas PM · 通用颜色选择器（P7.3，由 P4.5 项目类型 ColorField 提取）
 *
 * 预设色板 + 原生取色器（自定义色）。
 * ⚠️ 色板色值由调用方通过 `palette` 传入（业务数据）；选中态仅用于交互提示，走 tokens 变量。
 *
 * 通用化要点：
 *   · `palette`        —— 由调用方决定色板（项目类型传原 10 色；任务形态传 12 色并集）
 *   · `testIdPrefix`   —— 生成 `${prefix}-color-none` / `-color-option` / `-color-input`
 *   · `containerTestId`—— 可选的容器 testid（任务形态挂 `tc-field-color`）
 *   · 当前色不在色板中时，末尾动态追加一个以当前色填充、且为选中态的色块（A4.2）
 */

import { isHexColor } from './color-palette';
import './color-field.css';

export interface ColorFieldProps {
  value: string | null;
  onChange(value: string | null): void;
  /** 预设色板（业务数据，通常来自 color-palette.ts） */
  palette: readonly string[];
  /** testid 前缀，如 'pt' / 'tc' */
  testIdPrefix: string;
  /** 容器 data-testid（可选） */
  containerTestId?: string;
  /** 是否提供「无颜色」按钮，默认 true */
  allowNone?: boolean;
}

export default function ColorField({
  value,
  onChange,
  palette,
  testIdPrefix,
  containerTestId,
  allowNone = true,
}: ColorFieldProps) {
  const inputValue = isHexColor(value) ? value : (palette[0] ?? '#000000');
  const inPalette =
    value !== null && palette.some((c) => c.toLowerCase() === value.toLowerCase());
  const extraColor = isHexColor(value) && !inPalette ? value : null;

  return (
    <div className="cf" data-testid={containerTestId}>
      <div className="cf__swatches" role="listbox" aria-label="预设颜色">
        {allowNone && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-color-none`}
            className={['cf__swatch', 'cf__swatch--none', value === null ? 'is-on' : '']
              .filter(Boolean)
              .join(' ')}
            title="无颜色"
            aria-label="无颜色"
            onClick={() => onChange(null)}
          >
            无
          </button>
        )}

        {palette.map((c) => (
          <button
            key={c}
            type="button"
            data-testid={`${testIdPrefix}-color-option`}
            data-color={c}
            className={[
              'cf__swatch',
              value !== null && value.toLowerCase() === c.toLowerCase() ? 'is-on' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ background: c }}
            title={c}
            aria-label={`颜色 ${c}`}
            onClick={() => onChange(c)}
          />
        ))}

        {extraColor && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-color-option`}
            data-color={extraColor}
            className="cf__swatch is-on"
            style={{ background: extraColor }}
            title={extraColor}
            aria-label={`颜色 ${extraColor}`}
            onClick={() => onChange(extraColor)}
          />
        )}
      </div>

      <div className="cf__custom">
        <input
          type="color"
          data-testid={`${testIdPrefix}-color-input`}
          className="cf__input"
          value={inputValue}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="cf__value num">{value ?? '未设置'}</span>
      </div>
    </div>
  );
}
